import { Button, ResponsiveTable, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';

import { InlineEditCell } from '../../_ops/InlineEditCell.js';
import type { InventoryItemDto } from '../contractsMirror.js';

/**
 * Inventory list table (legacy `InventoryListPage` table). A real semantic `<table>` —
 * a select checkbox column (drives bulk archive) · Item (name + category sub, links to detail)
 * · Category · **Quantity** (inline-editable via `_ops/InlineEditCell`) · Status (active /
 * archived / low-stock, carried by TEXT not color alone, A11Y-01) · Actions — with a
 * `<caption>`, `<th scope="col">` headers, a select-all header checkbox, and every color a
 * locked token (no hex, BRAND-06).
 *
 * Quantity is the single-field inline edit (UI-SPEC "inline edit"): click → input → Enter →
 * `onAdjustQuantity` PATCHes + optimistically invalidates. The low-stock indicator is a labelled
 * badge (TEXT + icon), never color alone.
 */

const TEXT_TH =
  'px-3 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide';
const NUMERIC_TH =
  'px-3 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide';

export function InventoryTable({
  items,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onAdjustQuantity,
  adjustReasons,
  onAdjustReasonChange,
  onRestore,
  pendingItemId,
  restorePendingItemId,
}: {
  items: InventoryItemDto[];
  selectedIds: ReadonlySet<number>;
  onToggleSelect: (id: number) => void;
  onToggleSelectAll: () => void;
  onAdjustQuantity: (item: InventoryItemDto, nextQuantity: number) => void;
  /** The optional reason typed on each row, keyed by item id. */
  adjustReasons: Readonly<Record<number, string>>;
  onAdjustReasonChange: (itemId: number, reason: string) => void;
  onRestore?: (item: InventoryItemDto) => void;
  pendingItemId: number | null;
  restorePendingItemId?: number | null;
}) {
  const { t } = useTranslation();

  const selectableIds = items.filter((i) => !i.archivedAt).map((i) => i.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  return (
    <ResponsiveTable className="rounded-md border border-border bg-card">
      <table className="w-full border-collapse">
        <caption className="sr-only">{t('inventory:table.caption')}</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className="w-10 px-3 py-2">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={onToggleSelectAll}
                aria-label={t('inventory:table.selectAll')}
                className="size-4 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              />
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('inventory:table.item')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('inventory:table.category')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('inventory:table.quantity')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('inventory:table.status')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('inventory:table.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const archived = Boolean(item.archivedAt);
            return (
              <tr key={item.id} className="border-border/60 border-b last:border-b-0">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    disabled={archived}
                    onChange={() => onToggleSelect(item.id)}
                    aria-label={t('inventory:table.select', { name: item.name })}
                    className="size-4 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-40"
                  />
                </td>
                <td className="px-3 py-2 text-sm">
                  <Link
                    to="/inventory/$id"
                    params={{ id: String(item.id) }}
                    className="font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    {item.name}
                  </Link>
                </td>
                <td className="px-3 py-2 text-foreground text-sm">{item.categoryName}</td>
                <td className="px-3 py-2 text-right">
                  {archived ? (
                    <span className="font-mono text-foreground text-sm">
                      {item.quantity}
                      {item.quantityUnit ? ` ${item.quantityUnit}` : ''}
                    </span>
                  ) : (
                    <div className="ml-auto grid justify-items-end gap-2">
                      <InlineEditCell
                        value={String(item.quantity)}
                        label={t('inventory:actions.adjustQuantity', { name: item.name })}
                        type="number"
                        inputMode="decimal"
                        pending={pendingItemId === item.id}
                        className="ml-auto justify-end font-mono"
                        formatDisplay={(v) => (item.quantityUnit ? `${v} ${item.quantityUnit}` : v)}
                        onSave={(next) => {
                          const parsed = Number(next);
                          if (Number.isFinite(parsed) && parsed >= 0) {
                            onAdjustQuantity(item, parsed);
                          }
                        }}
                      />
                      <label className="grid justify-items-end gap-1">
                        <span className="sr-only">{t('inventory:adjust.reasonLabel')}</span>
                        <input
                          value={adjustReasons[item.id] ?? ''}
                          onChange={(event) => onAdjustReasonChange(item.id, event.target.value)}
                          maxLength={200}
                          placeholder={t('inventory:adjust.reasonPlaceholder')}
                          className="h-9 w-44 rounded-md border border-border bg-background px-2 text-foreground text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                        />
                      </label>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-sm">
                  {archived ? (
                    <span className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 font-medium text-muted-foreground text-xs">
                      {t('inventory:table.archived')}
                    </span>
                  ) : item.lowStock ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 font-medium text-destructive text-xs">
                      <AlertTriangle className="size-3" aria-hidden="true" />
                      {t('inventory:lowStock.badge')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 font-medium text-secondary-foreground text-xs">
                      {t('inventory:table.active')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {/* The item name is the accessible name only. It is what tells a screen-reader
                        user which of a list of near-identical rows this acts on, but printing it in
                        the visible label repeats the name column and swamps the actions cell. One
                        action and a View link is too thin to justify a MoreMenu here; the split is
                        the same one MoreMenu applies to its own trigger. */}
                    {archived && onRestore ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={restorePendingItemId === item.id}
                        onClick={() => onRestore(item)}
                        aria-label={t('inventory:actions.restoreItem', { name: item.name })}
                      >
                        {t('inventory:actions.restore')}
                      </Button>
                    ) : null}
                    <Link
                      to="/inventory/$id"
                      params={{ id: String(item.id) }}
                      className="inline-flex h-11 items-center rounded-md px-3 font-medium text-foreground text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {t('inventory:actions.view')}
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ResponsiveTable>
  );
}
